import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { ShoppingCart, CheckCircle2 } from "lucide-react";

/**
 * AuctionPayButton — v3.99d.414
 *
 * The old /auctions/:id/pay and /auctions/:id/payment-status endpoints
 * were retired in v3.99c.157. Auction wins now auto-merge into the
 * buyer's cart on GET /api/cart (see _sync_auction_wins_into_cart).
 *
 * This button:
 *   - Shows "Paid ✓" if the listing's auction_payment_status is "paid"
 *   - Otherwise navigates the winner to /cart so they can check out
 *     through the unified cart → checkout → Authorize.net pipeline.
 */
export default function AuctionPayButton({ listingId, compact = false }) {
  const [status, setStatus] = useState(null); // null = loading
  const nav = useNavigate();

  useEffect(() => {
    if (!listingId) return;
    // Touch the cart endpoint so auction wins get auto-synced,
    // then check the listing's payment status from the cart data.
    api.get("/cart")
      .then((r) => {
        const items = r.data?.items || r.data || [];
        const match = items.find((i) => i.listing_id === listingId);
        if (match) {
          setStatus({ in_cart: true, paid: false });
        } else {
          // Not in cart — might already be paid. Check listing directly.
          return api.get(`/listings/${listingId}`).then((lr) => {
            const ps = lr.data?.auction_payment_status;
            setStatus({ in_cart: false, paid: ps === "paid" });
          });
        }
      })
      .catch(() => {
        // Fallback: just show the go-to-cart button
        setStatus({ in_cart: false, paid: false });
      });
  }, [listingId]);

  if (!status) return null;

  if (status.paid) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border"
        data-testid={`auction-paid-${listingId}`}
        style={{
          background: "rgba(79,168,111,0.12)",
          color: "#7FCB99",
          borderColor: "#7FCB9955",
        }}
      >
        <CheckCircle2 className="w-3 h-3" /> Paid
      </span>
    );
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        nav("/cart");
      }}
      className="inline-flex items-center gap-1.5 btn-primary text-xs"
      data-testid={`auction-pay-now-${listingId}`}
    >
      <ShoppingCart className="w-3.5 h-3.5" />
      {compact ? "Pay" : "Go to cart to pay"}
    </button>
  );
}
